import { Component, HostListener, OnInit, Renderer2, ViewChildren, QueryList, ElementRef, AfterViewInit, ViewChild } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { LanguageService } from '../../services/language.service';
import { UserService, User } from '../../services/user.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-nav-bar',
    imports: [CommonModule, RouterModule, TranslateModule],
    templateUrl: './nav-bar.component.html',
    styleUrl: './nav-bar.component.scss'
})
export class NavBarComponent implements OnInit, AfterViewInit {

  isScrolled = false;
  isMenuOpen = false;
  isMobile = window.innerWidth <= 767;
  
  // Variables pour l'indicateur
  indicatorPosition = 0;
  indicatorWidth = 0;
  hasActiveLink = false;
  isLoggedIn = false;
  user: User | null = null;
  userDisplayName = '';
  
  @ViewChildren('navLink') navLinks!: QueryList<ElementRef>;
  @ViewChild('linksContainer') linksContainer!: ElementRef<HTMLElement>;

  constructor(
    public translate: TranslateService,
    private renderer: Renderer2,
    private router: Router,
  public languageService: LanguageService,
  private userService: UserService,
  private tokenStorage: TokenStorageService,
  private authService: AuthService
  ) {}

  ngOnInit() {
    this.checkScreenSize();
    this.updateBodyScroll();
    // Initialize auth state and user display name
    this.refreshAuthState();
    this.userService.currentUser.subscribe(u => {
      this.user = u;
      this.updateUserDisplayName();
    });
    
    // Écouter les changements de route pour mettre à jour l'indicateur
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      setTimeout(() => {
        this.adjustNavFontSize();
        this.updateIndicator();
      }, 100);
    });

    // Adjust on language change (translated labels length varies)
    this.translate.onLangChange.subscribe(() => {
      setTimeout(() => {
        this.adjustNavFontSize();
        this.updateIndicator();
      }, 50);
    });
  }
  
  ngAfterViewInit() {
    // Mettre à jour l'indicateur après le rendu initial
    setTimeout(() => {
      this.adjustNavFontSize();
      this.updateIndicator();
    }, 200);

    // Recompute when nav links list changes (rare but safe)
    this.navLinks.changes.subscribe(() => {
      setTimeout(() => {
        this.adjustNavFontSize();
        this.updateIndicator();
      }, 50);
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.scrollY > 20;
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
    if (!this.isMobile) {
      setTimeout(() => {
        this.adjustNavFontSize();
        this.updateIndicator();
      }, 100);
    }
  }

  checkScreenSize() {
    const wasAlreadyMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 767;
    
    // Si on passe de mobile à desktop, fermer le menu
    if (wasAlreadyMobile && !this.isMobile) {
      this.isMenuOpen = false;
      this.updateBodyScroll();
    }
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    this.updateBodyScroll();
  }

  closeMenuIfMobile() {
    if (this.isMobile) {
      this.isMenuOpen = false;
      this.updateBodyScroll();
    }
  }

  // Empêcher le défilement du body quand le menu mobile est ouvert
  private updateBodyScroll() {
    if (this.isMenuOpen) {
      this.renderer.setStyle(document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(document.body, 'overflow');
    }
  }
  
  // Mettre à jour la position et la largeur de l'indicateur
  updateIndicator() {
    if (this.isMobile) return;
    
    const activeLink = this.navLinks.find(link => 
      link.nativeElement.classList.contains('active')
    );
    
    if (activeLink) {
      const linkElement = activeLink.nativeElement;
      const rect = linkElement.getBoundingClientRect();
      const navRect = linkElement.parentElement.getBoundingClientRect();
      
      // Calculer la position relative par rapport au conteneur de navigation
      this.indicatorPosition = rect.left - navRect.left;
      this.indicatorWidth = rect.width;
      this.hasActiveLink = true;
    } else {
      // Aucun lien actif, masquer l'indicateur
      this.hasActiveLink = false;
    }
  }

  // Ajuster dynamiquement la taille de police des liens pour que tout tienne sur une ligne
  private adjustNavFontSize() {
    try {
      if (this.isMobile) return; // pas nécessaire sur mobile
      const container = this.linksContainer?.nativeElement;
      if (!container) return;

      // base et minimum (en px)
      const basePx = 15; // valeur par défaut dans le SCSS
      const minPx = 11;  // lisible mais compact

      // Réinitialiser la taille avant mesure
      container.style.setProperty('--nav-link-font-size', `${basePx}px`);

      const links: HTMLElement[] = this.navLinks?.toArray().map(el => el.nativeElement as HTMLElement) ?? [];
      if (!links.length) return;

      // Mesurer largeur nécessaire (liens + gaps)
      const styles = getComputedStyle(container);
      const gapStr = (styles.getPropertyValue('column-gap') || styles.getPropertyValue('gap') || '0').trim();
      const gap = parseFloat(gapStr) || 0;
      const linksWidth = links.reduce((sum, el) => sum + el.offsetWidth, 0);
      const totalGaps = gap * Math.max(links.length - 1, 0);
      const required = linksWidth + totalGaps + 2; // petite marge
      const available = container.clientWidth;

      if (required <= available) return; // rien à faire

      // Calculer l'échelle et appliquer via variable CSS
      const scale = Math.max(minPx / basePx, Math.min(1, available / required));
      const newPx = Math.floor(basePx * scale);
      container.style.setProperty('--nav-link-font-size', `${newPx}px`);
    } catch {
      // éviter toute casse si une mesure échoue
    }
  }
  
  // Générer l'URL correcte (sans préfixe de langue - traduction dynamique)
  getRouterLink(path: string): any[] {
    // Pour la page d'accueil
    if (path === '') {
      return ['/'];
    }
    // Pour les autres pages
    return ['/', path];
  }

  private refreshAuthState() {
    const token = this.tokenStorage.getToken();
    this.isLoggedIn = !!token;
    if (this.isLoggedIn && !this.user) {
      // Try to prime from session storage
      const stored = this.tokenStorage.getUser();
      if (stored && (stored.firstName || stored.lastName || stored.email)) {
        this.user = {
          id: stored.id || '',
          firstName: stored.firstName || '',
          lastName: stored.lastName || '',
          email: stored.email || ''
        } as User;
        this.updateUserDisplayName();
      }
      // Also try fetching the latest profile in background
      this.userService.getUserProfile().subscribe({
        next: u => {
          this.user = u;
          this.updateUserDisplayName();
        },
        error: () => {
          // ignore
        }
      });
    }
  }

  private updateUserDisplayName() {
    const u = this.user;
    if (u && (u.firstName || u.lastName)) {
      this.userDisplayName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    } else if (u?.email) {
      this.userDisplayName = u.email;
    } else {
      this.userDisplayName = '';
    }
  }

  goToDashboard() {
    this.router.navigate(['/dashboard']);
  }

  onLogout() {
    // Clear session storage and trigger app logout redirect
    this.tokenStorage.signOut();
    try { this.authService.logout(); } catch {}
  }
}